import { useEffect, useState, useCallback } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { SongCard } from "@/components/SongCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Songs() {
  const [meta, setMeta] = useState({ languages: [], genres: [], countries: [], moods: [] });
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ language: "", country: "", genre: "", mood: "", sort: "popular" });
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/meta").then(({ data }) => setMeta(data)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: 24, sort: filters.sort };
    if (q) params.q = q;
    ["language", "country", "genre", "mood"].forEach((k) => { if (filters[k]) params[k] = filters[k]; });
    try { const { data } = await api.get("/songs", { params }); setData(data); } finally { setLoading(false); }
  }, [q, filters, page]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [q, filters]);

  const setF = (k) => (v) => setFilters({ ...filters, [k]: v === "all" ? "" : v });

  const FilterSelect = ({ k, label, options }) => (
    <Select value={filters[k] || "all"} onValueChange={setF(k)}>
      <SelectTrigger className="bg-input border-white/10 rounded-full h-10 text-sm" data-testid={`filter-${k}`}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="glass border-white/10 max-h-64">
        <SelectItem value="all">All {label}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head text-4xl font-black tracking-tighter mb-2">Song Catalog</h1>
      <p className="text-muted-foreground mb-8">Millions of songs available through licensed catalog partners — starting with our royalty-free collection.</p>

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, artist or album..." data-testid="song-search"
          className="pl-12 h-14 bg-input border-white/10 rounded-full text-base" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <FilterSelect k="language" label="Languages" options={meta.languages} />
        <FilterSelect k="genre" label="Genres" options={meta.genres} />
        <FilterSelect k="country" label="Countries" options={meta.countries} />
        <FilterSelect k="mood" label="Moods" options={meta.moods} />
        <Select value={filters.sort} onValueChange={setF("sort")}>
          <SelectTrigger className="bg-input border-white/10 rounded-full h-10 text-sm" data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="glass border-white/10">
            <SelectItem value="popular">Most Popular</SelectItem>
            <SelectItem value="new">Newest</SelectItem>
            <SelectItem value="title">A–Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p className="text-sm text-muted-foreground mb-4" data-testid="song-count">{data.total} songs</p>
      {data.items.length === 0 && !loading ? (
        <p className="text-center text-muted-foreground py-20">No songs match your search.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {data.items.map((s, i) => <SongCard key={s.song_id} song={s} index={i} />)}
        </div>
      )}

      {data.pages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-10">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full glass border-white/10" data-testid="page-prev">Prev</Button>
          <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
          <Button variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-full glass border-white/10" data-testid="page-next">Next</Button>
        </div>
      )}
    </div>
  );
}
